import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';

type IdleCallbackScheduler = Pick<Window, 'cancelIdleCallback' | 'requestIdleCallback'>;

export type IdleScheduler = Partial<IdleCallbackScheduler> & Pick<Window, 'clearTimeout' | 'setTimeout'>;

function supportsIdleCallback(scheduler: IdleScheduler): scheduler is IdleCallbackScheduler & IdleScheduler {
  return typeof scheduler.requestIdleCallback === 'function' && typeof scheduler.cancelIdleCallback === 'function';
}

export function whenIdle(scheduler: IdleScheduler): Observable<void> {
  return new Observable<void>((subscriber) => {
    const emitIdle = (): void => {
      subscriber.next();
      subscriber.complete();
    };

    if (supportsIdleCallback(scheduler)) {
      const idleHandle = scheduler.requestIdleCallback(emitIdle);

      return (): void => scheduler.cancelIdleCallback(idleHandle);
    }

    const timeoutHandle = scheduler.setTimeout(emitIdle, 0);

    return (): void => scheduler.clearTimeout(timeoutHandle);
  });
}

export function isPreloadableRoute(route: Route): boolean {
  return route.data?.['preload'] === true;
}

@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!this.isBrowser || !isPreloadableRoute(route)) {
      return of(null);
    }

    return whenIdle(window).pipe(switchMap(() => load()));
  }
}
